import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, LayoutGrid, List, FileText, X } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Badge } from '../../../../../components/ui/Badge';
import { FilterBar, ExportMenu, MODULE_CONFIG } from '../shared';
import { Select } from '../../../../../components/ui/Select';
import { QuestionDetailPanel } from './QuestionDetailPanel';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
import type { IssueOwner, PipelineBatchResult } from '../../../../../governance';
import { deriveDisplaySeverity, resolveIssueOwnership } from '../../../../../governance';
import { QUESTION_TYPE_REGISTRY } from '../../../../../content/questions/questionTypeRegistry';
/**
 * Look up the human-readable Dutch label for a question type.
 * Falls back to "Onbekend" when the type is the 'unknown' sentinel
 * emitted by buildUnifiedRows (question lookup miss), or returns
 * the raw key when a registry entry is missing (defensive fallback).
 */
function getQuestionTypeLabel(type: string): string {
  if (type === 'unknown') return 'Onbekend';
  const entry = (QUESTION_TYPE_REGISTRY as Record<string, {
    label?: string;
  }>)[type];
  return entry?.label ?? type;
}
interface QuestionsTabProps {
  rows: UnifiedQuestionRow[];
  batchResult: PipelineBatchResult | null;
  isRunning: boolean;
  initialFilter?: string;
  onOpenQuestionInBank?: (row: UnifiedQuestionRow) => void;
}
type VerdictFilter = 'all' | 'PASS' | 'REVIEW_NEEDED' | 'FAIL';
type IssueFilter = 'all' | 'critical' | 'blockers' | 'review' | 'warnings' | 'info';
type ViewMode = 'cards' | 'table';
type OwnerFilter = 'all' | IssueOwner;
export function QuestionsTab({
  rows,
  batchResult,
  isRunning,
  initialFilter,
  onOpenQuestionInBank
}: QuestionsTabProps) {
  const [search, setSearch] = useState(initialFilter && initialFilter !== 'blockers' ? initialFilter : '');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>(initialFilter === 'blockers' ? 'FAIL' : 'all');
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('all');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  // Quick filters
  const [onlySeedIssues, setOnlySeedIssues] = useState(false);
  const [onlyVisualIssues, setOnlyVisualIssues] = useState(false);
  const [onlyPedagogyIssues, setOnlyPedagogyIssues] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;
  useEffect(() => {
    setSearch(initialFilter && initialFilter !== 'blockers' ? initialFilter : '');
    setVerdictFilter(initialFilter === 'blockers' ? 'FAIL' : 'all');
    setExpandedId(initialFilter && initialFilter !== 'blockers' ? rows.some(row => row.questionId === initialFilter) ? initialFilter : null : null);
  }, [initialFilter, rows]);
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      // Search
      if (search && !row.questionId.toLowerCase().includes(search.toLowerCase()) && !row.familyId.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      // Module
      if (moduleFilter !== 'all' && row.moduleCode !== moduleFilter) {
        return false;
      }
      // Verdict
      if (verdictFilter !== 'all' && row.verdict !== verdictFilter) {
        return false;
      }
      // Issue Type — Phase 1: 5-level display severity filter
      if (issueFilter !== 'all') {
        const hasMatching = row.pipelineIssues.some(i => {
          const ds = deriveDisplaySeverity(i.kind, i.severity);
          if (issueFilter === 'critical') return ds === 'critical';
          if (issueFilter === 'blockers') return ds === 'critical' || ds === 'blocker';
          if (issueFilter === 'review') return ds === 'review';
          if (issueFilter === 'warnings') return ds === 'coach';
          if (issueFilter === 'info') return ds === 'info';
          return false;
        });
        if (!hasMatching) return false;
      }
      // Remediation owner: a row stays visible when at least one finding
      // belongs to the selected route.
      if (ownerFilter !== 'all' && !row.pipelineIssues.some(issue => resolveIssueOwnership(issue).owner === ownerFilter)) {
        return false;
      }
      // Question Type
      if (typeFilter !== 'all' && row.questionType !== typeFilter) {
        return false;
      }
      // Quick Filters
      if (onlySeedIssues && row.seedWarnings.length === 0) return false;
      if (onlyVisualIssues && !row.hasVisualProblems) return false;
      if (onlyPedagogyIssues && !row.pipelineIssues.some(i => i.domain === 'pedagogy')) return false;
      return true;
    });
  }, [rows, search, moduleFilter, verdictFilter, issueFilter, ownerFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  // Detect "scoped to single seed file" — happens when user deep-links from
  // the Seed Prioritizer card on the Overzicht tab. We surface this clearly
  // so the admin knows they're currently triaging one seed at a time.
  const familyContext = useMemo(() => {
    if (!search || filteredRows.length === 0) return null;
    const uniqueFamilies = new Set(filteredRows.map(r => r.familyId));
    if (uniqueFamilies.size !== 1) return null;
    const familyId = filteredRows[0].familyId;
    // Only show the banner if the search term actually targets the family
    // (case-insensitive substring match). Otherwise this is just coincidence.
    if (!familyId.toLowerCase().includes(search.toLowerCase())) return null;
    const blockerCount = filteredRows.reduce((sum, r) => sum + r.blockerCount, 0);
    const warningCount = filteredRows.reduce((sum, r) => sum + r.warningCount, 0);
    const failCount = filteredRows.filter(r => r.verdict === 'FAIL').length;
    return {
      familyId,
      moduleCode: filteredRows[0].moduleCode,
      total: filteredRows.length,
      failCount,
      blockerCount,
      warningCount
    };
  }, [filteredRows, search]);
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, moduleFilter, verdictFilter, issueFilter, ownerFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, page]);
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const passCount = filteredRows.filter(r => r.verdict === 'PASS').length;
  const failCount = filteredRows.filter(r => r.verdict === 'FAIL').length;
  const reviewCount = filteredRows.filter(r => r.verdict === 'REVIEW_NEEDED').length;
  // Build type-filter options from the registry, sorted by Dutch label, and
  // include an "Onbekend" option ONLY when at least one row actually has the
  // 'unknown' fallback — that way the user can find broken rows without the
  // dropdown showing a phantom option in healthy datasets.
  const typeOptions = useMemo(() => {
    const registryEntries = Object.entries(QUESTION_TYPE_REGISTRY as Record<string, {
      label?: string;
    }>).map(([value, cfg]) => ({
      value,
      label: cfg.label ?? value
    })).sort((a, b) => a.label.localeCompare(b.label, 'nl'));
    const hasUnknown = rows.some(r => r.questionType === 'unknown');
    const options = [{
      value: 'all',
      label: 'Alle vraagtypes'
    }, ...registryEntries];
    if (hasUnknown) options.push({
      value: 'unknown',
      label: 'Onbekend'
    });
    return options;
  }, [rows]);
  if (isRunning) {
    return <div className="py-12 flex flex-col items-center justify-center space-y-4" data-id="element-5704" data-mp="274kdkbjkd8jc">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" data-id="element-5705" data-mp="276n2stcw6xn0"></div>
        <p className="text-gray-500 font-medium" data-id="element-5706" data-mp="1o5z2ynntxvuq">Data laden en valideren...</p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-5707" data-mp="1j7ebbcmn8khw">
      {/* Family-scope banner — visible when filtered to a single seed file */}
      {familyContext && <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-orange-200 bg-orange-50/70" data-id="element-5708" data-mp="1syigjkmioqzj">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${MODULE_CONFIG[familyContext.moduleCode]?.bgClass ?? 'bg-gray-400'}`} data-id="element-5709" data-mp="158o6fatl1giz" />
        
          <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" data-id="element-5710" data-mp="10cfq3u3nfha8" />
          <div className="min-w-0 flex-1" data-id="element-5711" data-mp="1ebp911j8ophw">
            <p className="text-sm font-semibold text-orange-900 truncate" data-id="element-5712" data-mp="19b18np94ewb0">
              Bekijk seed:{' '}
              <span className="font-mono" data-id="element-5713" data-mp="2pp170vsbidw5">{familyContext.familyId}</span>
            </p>
            <p className="text-xs text-orange-800/80 mt-0.5" data-id="element-5714" data-mp="2kq80xofd1p60">
              {familyContext.total}{' '}
              {familyContext.total === 1 ? 'vraag' : 'vragen'}
              {familyContext.failCount > 0 && ` · ${familyContext.failCount} afgekeurd`}
              {familyContext.blockerCount > 0 && ` · ${familyContext.blockerCount} blocker${familyContext.blockerCount === 1 ? '' : 's'}`}
              {familyContext.warningCount > 0 && ` · ${familyContext.warningCount} verbeterpunt${familyContext.warningCount === 1 ? '' : 'en'}`}
            </p>
          </div>
          <button type="button" onClick={() => setSearch('')} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-700 hover:text-orange-900 bg-white border border-orange-200 hover:bg-orange-100 rounded-lg transition-colors flex-shrink-0" data-id="element-5715" data-mp="3byhy32j0brku">
          
            <X className="w-3 h-3" data-id="element-5716" data-mp="375otqfrgal9a" />
            Wis filter
          </button>
        </div>}

      {/* Filters */}
      <FilterBar moduleFilter={moduleFilter} onModuleFilter={setModuleFilter} search={search} onSearch={setSearch} data-id="element-5717" data-mp="3uuzkr5j011a8">
        
        <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} options={typeOptions} size="sm" fullWidth={false} data-id="element-5718" data-mp="3jpwx4cq5orb" />
        
        <Select value={verdictFilter} onChange={e => setVerdictFilter(e.target.value as VerdictFilter)} options={[{
        value: 'all',
        label: 'Alle statussen'
      }, {
        value: 'PASS',
        label: 'Geslaagd (PASS)'
      }, {
        value: 'REVIEW_NEEDED',
        label: 'Review (REVIEW)'
      }, {
        value: 'FAIL',
        label: 'Afgekeurd (FAIL)'
      }]} size="sm" fullWidth={false} data-id="element-5719" data-mp="29mo184x66cfa" />
        
        <Select value={issueFilter} onChange={e => setIssueFilter(e.target.value as IssueFilter)} options={[{
        value: 'all',
        label: 'Alle bevindingen'
      }, {
        value: 'critical',
        label: '🔴 Heeft kritieke'
      }, {
        value: 'blockers',
        label: '🟠 Heeft blockers'
      }, {
        value: 'review',
        label: '🟡 Heeft review'
      }, {
        value: 'warnings',
        label: '🔵 Heeft coach'
      }, {
        value: 'info',
        label: '⚪ Heeft info'
      }]} size="sm" fullWidth={false} data-id="element-5720" data-mp="1qp4ux923j7wk" />
        
        <Select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value as OwnerFilter)} options={[{
        value: 'all',
        label: 'Alle eigenaren'
      }, {
        value: 'seed',
        label: 'Seed'
      }, {
        value: 'rulebook',
        label: 'Rulebook'
      }, {
        value: 'platform-registry',
        label: 'Platform/registry'
      }, {
        value: 'review-first',
        label: 'Eerst beoordelen'
      }]} size="sm" fullWidth={false} data-id="element-5721" data-mp="jcz7alr9bzzr" />
        
      </FilterBar>

      {/* Quick Filters & Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm" data-id="element-5722" data-mp="1h20t7eba2x1b">
        <div className="flex flex-wrap items-center gap-2" data-id="element-5723" data-mp="1q04ce3ysjhpy">
          <button onClick={() => setOnlySeedIssues(!onlySeedIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlySeedIssues ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5724" data-mp="1py9tswazs1p1">
            
            Seed Issues
          </button>
          <button onClick={() => setOnlyVisualIssues(!onlyVisualIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyVisualIssues ? 'bg-purple-100 text-purple-800 border-purple-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5725" data-mp="23055j0d8iasv">
            
            Visual Issues
          </button>
          <button onClick={() => setOnlyPedagogyIssues(!onlyPedagogyIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyPedagogyIssues ? 'bg-indigo-100 text-indigo-800 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5726" data-mp="2gtwfuayshypn">
            
            Pedagogie Issues
          </button>

          <div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block" data-id="element-5727" data-mp="wczvd3qug2vt"></div>

          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-id="element-5728" data-mp="39mrmbqiocrtq">
            <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Tabelweergave" data-id="element-5729" data-mp="34vjc4s9it2fi">
              
              <List className="w-4 h-4" data-id="element-5730" data-mp="2vvw7zypepiir" />
            </button>
            <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Kaartweergave" data-id="element-5731" data-mp="3sl3zdncpilul">
              
              <LayoutGrid className="w-4 h-4" data-id="element-5732" data-mp="3jj4dohqqytlb" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm" data-id="element-5733" data-mp="3ekjcaezre3va">
          <div className="flex items-center gap-3 text-gray-600" data-id="element-5734" data-mp="284z85kavybnv">
            <span className="font-semibold text-gray-900" data-id="element-5735" data-mp="286jzz25tfgi1">
              {filteredRows.length}
            </span>{' '}
            vragen
            <span className="text-emerald-600 font-medium" data-id="element-5736" data-mp="3r33txkxuqjjz">
              {passCount} geslaagd
            </span>
            <span className="text-red-600 font-medium" data-id="element-5737" data-mp="3vtu193pu8whb">
              {failCount} afgekeurd
            </span>
            <span className="text-amber-600 font-medium" data-id="element-5738" data-mp="3i38f965386f7">
              {reviewCount} review
            </span>
          </div>

          {batchResult && <ExportMenu results={batchResult.results.filter(r => filteredRows.some(fr => fr.questionId === r.questionId))} filePrefix="governance-vragen" data-id="element-5739" data-mp="3myw5lovb35ma" />}
        </div>
      </div>

      {/* Table / Cards */}
      <div className="space-y-2" data-id="element-5740" data-mp="ikpkj6l7a2hb">
        {paginatedRows.length === 0 ? <div className="rounded-xl border border-gray-200 bg-white py-12 text-center" data-id="element-5741" data-mp="4mr9aa26em61">
            <p className="text-gray-500" data-id="element-5742" data-mp="9l2hyhgqiqmo">
              Geen vragen gevonden voor deze filters.
            </p>
          </div> : viewMode === 'table' ? <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5743" data-mp="2pfuo5oo2m530">
            <div className="overflow-x-auto" data-id="element-5744" data-mp="2ua8hzmn3445k">
              <table className="w-full text-left text-sm" data-id="element-5745" data-mp="2ms0vqw8vciel">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-medium" data-id="element-5746" data-mp="2htzaq5rvu90h">
                  <tr data-id="element-5747" data-mp="2vtrv7jctft00">
                    <th className="px-4 py-3" data-id="element-5748" data-mp="2vu0atj0bpcew">ID</th>
                    <th className="px-4 py-3" data-id="element-5749" data-mp="2vrnu196ax83f">Status</th>
                    <th className="px-4 py-3" data-id="element-5750" data-mp="2qvqh7cu8yjve">Score</th>
                    <th className="px-4 py-3" data-id="element-5751" data-mp="2352sxh00xhmo">Issues</th>
                    <th className="px-4 py-3" data-id="element-5752" data-mp="235w18memsf6x">Type</th>
                    <th className="px-4 py-3" data-id="element-5753" data-mp="1z3rdu5es0esq">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-5754" data-mp="2cumg2yurrxnc">
                  {paginatedRows.map(row => {
                const isExpanded = expandedId === row.questionId;
                const modConfig = MODULE_CONFIG[row.moduleCode] || {
                  bgClass: 'bg-gray-500'
                };
                const statusConfig = {
                  PASS: {
                    bg: 'bg-emerald-50',
                    text: 'text-emerald-700',
                    icon: CheckCircle2,
                    label: 'PASS'
                  },
                  REVIEW_NEEDED: {
                    bg: 'bg-amber-50',
                    text: 'text-amber-700',
                    icon: AlertTriangle,
                    label: 'REVIEW'
                  },
                  FAIL: {
                    bg: 'bg-red-50',
                    text: 'text-red-700',
                    icon: XCircle,
                    label: 'FAIL'
                  }
                }[row.verdict];
                const StatusIcon = statusConfig.icon;
                return <Fragment key={row.questionId} data-id="element-5755" data-mp="36fs1rq421crt">
                        <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/30' : ''}`} onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-5756" data-mp="3aj6kwpnai99h">
                        
                          <td className="px-4 py-3" data-id="element-5757" data-mp="predplxyoi8w">
                            <div className="flex items-center gap-2" data-id="element-5758" data-mp="2aagusw0j0i41">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-5759" data-mp="2a9ox9fndcab4" />
                            
                              <span className="font-mono font-semibold text-gray-900" data-id="element-5760" data-mp="21hwt1moxtxez">
                                {row.questionId}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5761" data-mp="1rks4jg7mdpv0">
                            <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-5762" data-mp="1mkw1pkans4h1">
                            
                              <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-5763" data-mp="1ileuomfl2z9r" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-5764" data-mp="3cwk3041m7zvf">
                            <div className="flex items-center gap-2 w-24" data-id="element-5765" data-mp="37xz2s4md5g6z">
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5766" data-mp="2gswk5gqvu6bx">
                                <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                            width: `${row.pipelineScore}%`
                          }} data-id="element-5767" data-mp="2lrpwv6ychtb1" />
                              
                              </div>
                              <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-5768" data-mp="1y1lu11szdxl3">
                                {row.pipelineScore}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5769" data-mp="1t3akytaifr3x">
                            <div className="flex items-center gap-1.5 text-xs" data-id="element-5770" data-mp="1t3u1c5kii7fu">
                              {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-5771" data-mp="3iirvx87wos90">
                                  <XCircle className="w-3 h-3" data-id="element-5772" data-mp="1btzks0i9nvw9" />{' '}
                                  {row.blockerCount}
                                </span>}
                              {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5773" data-mp="16ssey6zop6s8">
                                  <AlertTriangle className="w-3 h-3" data-id="element-5774" data-mp="1gsj6q0wv17om" />{' '}
                                  {row.warningCount}
                                </span>}
                              {row.blockerCount === 0 && row.warningCount === 0 && <span className="text-gray-400" data-id="element-5775" data-mp="1pkctw66w9gn6">-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5776" data-mp="24gkmnuwdbqe2">
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${row.questionType === 'unknown' ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-gray-600 bg-gray-50 border-gray-200'}`} title={row.questionType === 'unknown' ? 'Vraag niet gevonden in content registry — mogelijk verouderde batch.' : row.questionType} data-id="element-5777" data-mp="1zjaptu6ngcg7">
                            
                              {getQuestionTypeLabel(row.questionType)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-5778" data-mp="27zmpniw83vvi">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 inline" data-id="element-5779" data-mp="2311p1id27a6j" /> : <ChevronRight className="w-4 h-4 text-gray-400 inline" data-id="element-5780" data-mp="233o1nwet4fvm" />}
                          </td>
                        </tr>
                        {isExpanded && <tr data-id="element-5781" data-mp="1adyz3emg87rw">
                            <td colSpan={6} className="p-0 border-t border-gray-100" data-id="element-5782" data-mp="1af0efvahzgff">
                          
                              <QuestionDetailPanel row={row} onOpenQuestionInBank={onOpenQuestionInBank} data-id="element-5783" data-mp="1p7tvvm9by6mq" />
                          
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div> : paginatedRows.map(row => {
        const isExpanded = expandedId === row.questionId;
        const modConfig = MODULE_CONFIG[row.moduleCode] || {
          bgClass: 'bg-gray-500'
        };
        const statusConfig = {
          PASS: {
            bg: 'bg-emerald-50',
            border: 'border-emerald-200',
            text: 'text-emerald-700',
            icon: CheckCircle2,
            label: 'PASS'
          },
          REVIEW_NEEDED: {
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            text: 'text-amber-700',
            icon: AlertTriangle,
            label: 'REVIEW'
          },
          FAIL: {
            bg: 'bg-red-50',
            border: 'border-red-200',
            text: 'text-red-700',
            icon: XCircle,
            label: 'FAIL'
          }
        }[row.verdict];
        const StatusIcon = statusConfig.icon;
        return <Card key={row.questionId} className={`overflow-hidden transition-colors border ${isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5784" data-mp="dkftp8dsqxjg">
              
                <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer bg-white" onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-5785" data-mp="jeuq0cvkm5i3">
                
                  {/* Left: ID & Status */}
                  <div className="flex items-center gap-3 min-w-0" data-id="element-5786" data-mp="9g5b8syz1162">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-5787" data-mp="4hu1ei8oeqnu" />
                  
                    <div className="flex items-center gap-3 min-w-0" data-id="element-5788" data-mp="3vvg7kxomefkc">
                      <span className="font-mono text-sm font-semibold text-gray-900 truncate" data-id="element-5789" data-mp="3ru4q5ffwh0n8">
                        {row.questionId}
                      </span>
                      <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-5790" data-mp="3mtqhbv6h5mo5">
                      
                        <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-5791" data-mp="3cxx74ig8a59j" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Right: Score & Issues */}
                  <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0" data-id="element-5792" data-mp="3crmse4gqdq9j">
                    {/* Issues Summary */}
                    <div className="flex items-center gap-2 text-xs" data-id="element-5793" data-mp="3hqzeuo3owvbl">
                      {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-5794" data-mp="2k3h457qgxj4y">
                          <XCircle className="w-3 h-3" data-id="element-5795" data-mp="2p2tui2gjt8ca" /> {row.blockerCount}
                        </span>}
                      {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5796" data-mp="2tz24l1fw2bcl">
                          <AlertTriangle className="w-3 h-3" data-id="element-5797" data-mp="21cpnrm82csh2" />{' '}
                          {row.warningCount}
                        </span>}
                      {row.seedWarnings.length > 0 && <span className="flex items-center gap-1 text-purple-600 font-medium bg-purple-50 px-1.5 py-0.5 rounded" title="Seed warnings" data-id="element-5798" data-mp="2697qyhnqwira">
                      
                          🌱 {row.seedWarnings.length}
                        </span>}
                    </div>

                    {/* Score Bar */}
                    <div className="hidden sm:flex items-center gap-2 w-24" data-id="element-5799" data-mp="guowxnv35lo8">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5800" data-mp="gslx6c44se6b">
                        <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${row.pipelineScore}%`
                  }} data-id="element-5801" data-mp="unod7s2ozjwu" />
                      
                      </div>
                      <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-5802" data-mp="zj3occ9bf1pk">
                        {row.pipelineScore}%
                      </span>
                    </div>

                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-5803" data-mp="31emgoqfx3y53" /> : <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-5804" data-mp="1gvacc0jp057y" />}
                  </div>
                </div>

                {/* Expanded Detail Panel */}
                {isExpanded && <QuestionDetailPanel row={row} onOpenQuestionInBank={onOpenQuestionInBank} data-id="element-5805" data-mp="174z627cjdjxw" />}
              </Card>;
      })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200" data-id="element-5806" data-mp="y3r6llyaad28">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5807" data-mp="132kp5ssfgu99">
          
            Vorige
          </button>
          <span className="text-sm text-gray-600" data-id="element-5808" data-mp="2ij2z9bbav41w">
            Pagina <span className="font-semibold text-gray-900" data-id="element-5809" data-mp="2ihi17bps12za">{page}</span>{' '}
            van {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5810" data-mp="2m1ikca2gblxj">
          
            Volgende
          </button>
        </div>}
    </div>;
}